import { Component, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { FormComponentBase } from '../../../core/component/forms/form-component.base';
import { FormControlEx } from '../../../core/component/forms/form-control';
import { standardCommand } from '../../../core/model/command';
import { DataService } from '../../../core/service/base/data.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { DeleteFormCommandHandler } from '../../../core/service/command/delete-form-command-handler.service';
import { FormCommandHandlerResolver } from '../../../core/service/command/form-command-handler-resolver.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { UserGroup } from '../../model/user-group';
import { UserGroupService } from '../../service/data/user-group.service';

@Component({
    selector: 'app-user-group-form',
    templateUrl: './user-group-form.component.html',
    styleUrls: ['./user-group-form.component.scss'],
    providers: [DeleteFormCommandHandler, FormCommandHandlerResolver, { provide: DataService, useExisting: UserGroupService }],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class UserGroupFormComponent extends FormComponentBase<UserGroup> implements OnDestroy {
  private readonly _fullAccessValue: string = 'Admin';
  private readonly _fullModuleAccessPrefix: string = 'Admin_';
  private readonly _publicApiModulePrefix: string = 'PLAPI.';

  constructor(
    private readonly _fcf: FormControlExFactory,
    activatedRoute: ActivatedRoute,
    private readonly _dataService: UserGroupService,
    dispatcher: CommandDispatcher,
    handlerResolver: FormCommandHandlerResolver,
    messageService: MessageService,
    navigation: NavigationService
  ) {
    super(activatedRoute, _dataService, messageService, navigation, dispatcher, handlerResolver);
    this.addMenuItemDefinition('CREATE_BTN', standardCommand.create);
  }

  protected createModel(): UserGroup {
    return new UserGroup();
  }

  protected init(data: UserGroup) {
    this.AddSubscription(
      this._dataService.getFeatures().subscribe(result => {
        if (data.systemGroup) {
          let modules = result.filter(m => m.features.filter(f => data.features.includes(f)).length > 0);
          for (let module of modules) module.features = module.features.filter(f => data.features.includes(f));
        } else this.modules = result;

        super.init(data);

        this.allFeatures.forEach(feature => {
          const control = this._featureControlMap.get(feature);
          if (control) {
            this._updateControlStatesFor(feature, !!control.value);
          }
        });
      })
    );
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._moduleSubscription?.unsubscribe();
  }

  private _moduleSubscription: Subscription = new Subscription();
  private _featureControlMap = new Map<string, FormControlEx<any>>();
  modules: { name: string; features: string[] }[];

  get standardModules(): { name: string; features: string[] }[] {
    return this.modules?.filter(m => !m.name.startsWith(this._publicApiModulePrefix)) ?? [];
  }

  get publicApiModules(): { name: string; features: string[] }[] {
    return this.modules?.filter(m => m.name.startsWith(this._publicApiModulePrefix)) ?? [];
  }

  private get allFeatures(): string[] {
    let features: string[] = [];
    for (let module of this.modules) features = features.concat(module.features);
    return features;
  }

  protected getFormGroup(): FormGroup {
    const group = new UntypedFormGroup({
      name: this._fcf.createMandatory('USERGROUP.NAME', this.dataContext.name),
    });
    const features = this.dataContext.systemGroup ? this.dataContext.features : this.allFeatures;
    for (let feature of features) {
      const value = this.dataContext.features.includes(feature);
      const control = this._fcf.create('FEATURE.NAMES.' + feature.toUpperCase(), value);
      this._featureControlMap.set(feature, control);

      this._moduleSubscription.add(
        control.valueChanges.subscribe(newValue => {
          this._updateControlStatesFor(feature, !!newValue);
        })
      );

      group.addControl(feature, control);
    }
    return group;
  }

  protected prepareSave(): UserGroup {
    const model = super.prepareSave();
    model.features = this.prepareFeatures();
    return model;
  }

  private prepareFeatures(): string[] {
    const values = this.form.value;
    return this.allFeatures.filter(f => values[f]);
  }

  protected override formSubmittedChange(): void {
    // update current dataContext after successfully saving changes, so fullAccess features reset their children correctly
    this.dataContext.features = this.prepareFeatures();
    super.formSubmittedChange();
  }

  private _updateControlStatesFor(feature: string, newValue: boolean): void {
    // if the newValue is the same as the initial untouched value, then reset the control ("un-dirty"):
    if (!!newValue === this.dataContext.features.includes(feature)) {
      const control = this._featureControlMap.get(feature);
      if (control.dirty) {
        control.reset(!!newValue, { emitEvent: false });
      }
    }

    // if this is a "full-access" type of feature, then update child feature states:
    if (feature === this._fullAccessValue) {
      if (newValue) {
        const features = this.allFeatures.filter(x => x !== feature);
        this._childFeaturesFullAccess(features);
      } else {
        const features = this.allFeatures.filter(f => f.startsWith(this._fullModuleAccessPrefix));
        this._childFeaturesReset(features, true); // <-- reset the admin-features, with recursive processing
      }
    } else if (feature.startsWith(this._fullModuleAccessPrefix)) {
      const module = this.modules.find(m => m.features.includes(feature));
      const features = module.features.filter(x => x !== feature);

      if (newValue) {
        this._childFeaturesFullAccess(features);
      } else {
        this._childFeaturesReset(features);
      }
    }
  }

  private _childFeaturesFullAccess(childFeatures: string[]): void {
    // Full Access on: Disable all child feature controls (and toggle them to "on" state)
    childFeatures.forEach(feature => {
      const control = this._featureControlMap.get(feature);
      control.setValue(true, { emitEvent: false });
      control.disable({ emitEvent: false });
    });
  }

  private _childFeaturesReset(childFeatures: string[], recursive: boolean = false): void {
    // Full Access off: Enable all child feature controls (and reset them to initial untouched state)
    childFeatures.forEach(feature => {
      const initialValue = this.dataContext.features.includes(feature);
      const control = this._featureControlMap.get(feature);
      control.reset(initialValue, { emitEvent: false });
      control.enable({ emitEvent: false });
      if (recursive) {
        this._updateControlStatesFor(feature, initialValue);
      }
    });
  }
}
